import { Eye, FileText, Gear, Swap, X } from '@phosphor-icons/react';
import { useState, type DragEvent } from 'react';
import { StatusDot } from '~/features/chat/chat-lamp';
import type { BarChatState } from '~/features/chat/top-bar/state-mapping';
import { Tip } from '~/components/ui/tip';
import { findWindow, needsChoice, type WindowEntry } from '~/lib/stores/window-rules';
import { useWindowStore } from '~/lib/stores/window-store';
import { cn } from '~/lib/utils';
import { WINDOW_KIND_LABEL } from './window-titles';
import { SlotKey } from '~/features/chat/top-bar/slot-key';


/**
 * FRONT-3146: every open window as a tab in the top bar (eddy, 2026-10-26 —
 * after trying a bottom bar and then a sidebar list). One tab per open window
 * in the model's order — new ones on the right, and the person drags a tab to
 * reorder; the columns below follow the same order (eddy, 2026-10-27): status dot (grey idle, blue working, amber
 * waiting), truncated title; a shown window
 * gets the light grey background, nothing else changes. Clicking a tab only
 * toggles the window shown / minimized — there is no close here; a window
 * closes from its own title bar. Restoring obeys the two-visible limit, so it
 * may start the "click the column to replace" pick. A window that is an open
 * question is left out here — it stays a question chip, lit while shown, so
 * nothing moves between the two areas on a toggle.
 */
export function WindowTabs({
  exclude,
  states,
  firstSlot,
}: {
  /** The ⌥ number of the first tab (the question chips come before it). */
  firstSlot?: number;
  exclude?: ReadonlySet<string>;
  /** Each window's chat / Build state, for its status dot (grey idle, blue working, amber waiting). */
  states?: ReadonlyMap<string, BarChatState>;
}) {
  // The stable array and plain values, never a derived array (window-selectors.test.ts).
  const windows = useWindowStore((s) => s.windows);
  const choosing = useWindowStore((s) => needsChoice(s));
  const pendingTitle = useWindowStore((s) => (s.pendingId ? findWindow(s, s.pendingId)?.title : undefined));
  return <WindowTabsView windows={windows} exclude={exclude} states={states} choosing={choosing} pendingTitle={pendingTitle} firstSlot={firstSlot} />;
}

/**
 * The tabs in the order the bar shows them — the one definition of that order,
 * read by the markup below and by the ⌥1–⌥9 shortcut (eddy, 2026-10-26).
 */
export function placedTabs(
  windows: readonly WindowEntry[],
  exclude: ReadonlySet<string> | undefined,
  choosing: boolean,
): WindowEntry[] {
  return (
    windows
      .filter((w) => !exclude?.has(w.id))
      // While picking the window to replace, only the choices stay — plus the
      // new window itself, which must not vanish (eddy, 2026-10-26: "hide all
      // other tabs", "myself should not be hidden"). Outside a pick, a waiting
      // window does not exist yet.
      .filter((w) => (choosing ? w.state === 'visible' || w.state === 'pending' : w.state !== 'pending'))
  );
}

/** The drag payload of a tab being reordered: the window's id. */
const TAB_DRAG_TYPE = 'application/x-eddy-window-tab';

/** The tabs' markup over a given window list (the connector above reads the store). */
export function WindowTabsView({
  windows,
  exclude,
  states,
  choosing = false,
  pendingTitle,
  firstSlot,
}: {
  firstSlot?: number;
  windows: readonly WindowEntry[];
  states?: ReadonlyMap<string, BarChatState>;
  /** Windows that are open questions — they stay as question chips instead. */
  exclude?: ReadonlySet<string>;
  /**
   * A third window is waiting: a SHOWN tab becomes "replace this one" (swap
   * icon, click replaces it) and a minimized tab rests (eddy, 2026-10-26: "let
   * me choose one of the open tabs directly", "change the icon").
   */
  choosing?: boolean;
  pendingTitle?: string;
}) {
  // Order and style never change when a window is toggled (eddy, 2026-10-26);
  // only a drag reorders, and the columns follow it (eddy, 2026-10-27).
  const placed = placedTabs(windows, exclude, choosing);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<{ id: string; side: 'before' | 'after' } | null>(null);
  if (placed.length === 0) return null;
  // Our own transfer type, never `Files`: a tab drag must not light up the
  // upload drop zones, and a real file drag must not reorder tabs.
  const isTabDrag = (e: DragEvent) => e.dataTransfer.types.includes(TAB_DRAG_TYPE);
  const sideOf = (e: DragEvent<HTMLElement>): 'before' | 'after' => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientX < r.left + r.width / 2 ? 'before' : 'after';
  };
  const endDrag = () => {
    setDragId(null);
    setDropAt(null);
  };
  return (
    <ul aria-label="Open windows" data-testid="window-tabs" className="flex min-w-0 shrink items-center gap-[6px]">
      {placed.map((w, i) => {
        const shown = w.state === 'visible';
        const label = w.title || WINDOW_KIND_LABEL[w.kind];
        const waiting = w.state === 'pending';
        const replacing = choosing && shown;
        const resting = choosing && !shown && !waiting;
        const closeTab = () => {
          if (waiting) useWindowStore.getState().cancelPending();
          else useWindowStore.getState().close(w.id);
        };
        return (
          <li
            key={w.id}
            data-testid="window-tab"
            data-window-id={w.id}
            data-state={w.state}
            // Drag a tab to reorder (eddy, 2026-10-27). Not while picking a
            // window to replace — those tabs are choices, not a list to arrange.
            draggable={!choosing}
            onDragStart={(e) => {
              e.dataTransfer.setData(TAB_DRAG_TYPE, w.id);
              e.dataTransfer.effectAllowed = 'move';
              setDragId(w.id);
            }}
            onDragOver={(e) => {
              if (!isTabDrag(e) || dragId === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              const side = sideOf(e);
              if (dropAt?.id !== w.id || dropAt.side !== side) setDropAt({ id: w.id, side });
            }}
            onDragLeave={(e) => {
              if (dropAt?.id === w.id && !e.currentTarget.contains(e.relatedTarget as Node | null)) setDropAt(null);
            }}
            onDrop={(e) => {
              if (!isTabDrag(e)) return;
              e.preventDefault();
              const id = e.dataTransfer.getData(TAB_DRAG_TYPE);
              if (id) useWindowStore.getState().move(id, w.id, sideOf(e));
              endDrag();
            }}
            onDragEnd={endDrag}
            // Middle click closes it, as on browser tabs (eddy, 2026-10-26).
            // mousedown stops the browser's autoscroll from starting first.
            onMouseDown={(e) => {
              if (e.button === 1) e.preventDefault();
            }}
            onAuxClick={(e) => {
              if (e.button !== 1 || (choosing && !waiting)) return;
              e.preventDefault();
              closeTab();
            }}
            // Double-click closes it too; on the waiting new window it cancels
            // it (eddy, 2026-10-26). Never a replace target — its single click
            // already chose.
            onDoubleClick={() => {
              if (choosing && !waiting) return;
              closeTab();
            }}
            // The light grey "active" background is the ONLY thing that differs
            // between a shown and a minimized tab (eddy, 2026-10-26).
            className={cn(
              'group relative flex h-[38px] min-w-[96px] max-w-[200px] shrink items-center rounded-[11px] text-[var(--ink-1)] transition-colors hover:bg-[var(--bg-3)]',
              shown && 'bg-[var(--bg-3)]',
              dragId === w.id && 'opacity-50',
              // The new window waiting for a place: outlined, not filled.
              waiting && 'border border-dashed border-primary/50',
            )}
          >
            {dropAt?.id === w.id && dragId !== w.id && (
              // Where the dragged tab will land: a bar in the gap on that side.
              <span
                aria-hidden
                data-testid="window-tab-drop-marker"
                className={cn(
                  // Full tab height and then some, centred in the 6px gap, so it
                  // reads at a glance (eddy, 2026-10-27: "a vertical drop marker").
                  'pointer-events-none absolute -inset-y-[3px] z-10 w-[3px] rounded-full bg-primary shadow-[0_0_0_2px_var(--background)]',
                  dropAt.side === 'before' ? '-left-[4.5px]' : '-right-[4.5px]',
                )}
              />
            )}
            {waiting ? (
              <Tip label="New — pick a window to replace, or double-click to cancel">
                <span
                  data-testid="window-tab-waiting"
                  role="img"
                  aria-label={`${label} (new, waiting for a place — double-click to cancel)`}
                  className="flex min-w-0 flex-1 cursor-default items-center gap-[7px] px-[11px] text-[13px]"
                >
                  <StatusDot state={undefined} />
                  <span className="min-w-0 truncate">{label}</span>
                </span>
              </Tip>
            ) : choosing ? (
              <Tip label={replacing ? `Replace ${label}${pendingTitle ? ` with ${pendingTitle}` : ''}` : shown ? `Minimize ${label}` : `Open ${label}`}>
                <button
                  type="button"
                  data-testid={replacing ? 'window-tab-replace' : 'window-tab-toggle'}
                  aria-pressed={shown}
                  aria-disabled={resting || undefined}
                  aria-label={
                    replacing
                      ? `Replace ${label}${pendingTitle ? ` with ${pendingTitle}` : ''}`
                      : `${WINDOW_KIND_LABEL[w.kind]}: ${label}${shown ? ' (shown)' : ' (minimized)'}`
                  }
                  onClick={() => {
                    if (resting) return;
                    if (replacing) useWindowStore.getState().chooseReplace(w.id);
                    else useWindowStore.getState().toggle(w.id);
                  }}
                  className="flex min-w-0 flex-1 items-center gap-[7px] rounded-[11px] py-1 px-[11px] text-left text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:opacity-50"
                >
                  {replacing ? (
                    <Swap size={14} weight="bold" className="shrink-0 text-primary" data-testid="window-tab-swap-icon" />
                  ) : (
                    // A status dot, not a kind icon (eddy, 2026-10-26) — except the
                    // Settings windows, which have no chat state to show.
                    w.kind === 'settings' || w.kind === 'system-settings' ? (
                      <Gear size={14} className="shrink-0" />
                    ) : w.kind === 'file' ? (
                      <FileText size={14} className="shrink-0" />
                    ) : w.kind === 'app' ? (
                      <Eye size={14} className="shrink-0" />
                    ) : (
                      <StatusDot state={states?.get(w.id)} />
                    )
                  )}
                  <span className="min-w-0 truncate">{label}</span>
                </button>
              </Tip>
            ) : (
              <>
                {/* The status dot turns into a close × while the tab is hovered
                    or focused (eddy, 2026-10-26: "I want to be able to close it
                    in the top bar, change the icon accordingly"). */}
                <Tip label={`Close ${label}`}>
                  <button
                    type="button"
                    data-testid="window-tab-close"
                    aria-label={`Close ${label}`}
                    onClick={() => useWindowStore.getState().close(w.id)}
                    className="ml-[5px] inline-flex size-6 shrink-0 items-center justify-center rounded-md outline-none hover:bg-background focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span data-testid="window-tab-indicator" className="contents group-hover:hidden group-focus-within:hidden">
                      {w.kind === 'settings' || w.kind === 'system-settings' ? (
                        <Gear size={14} className="shrink-0" />
                      ) : w.kind === 'file' ? (
                        <FileText size={14} className="shrink-0" />
                      ) : w.kind === 'app' ? (
                        <Eye size={14} className="shrink-0" />
                      ) : (
                        <StatusDot state={states?.get(w.id)} />
                      )}
                    </span>
                    <X size={12} aria-hidden className="hidden group-hover:block group-focus-within:block" />
                  </button>
                </Tip>
                <Tip label={shown ? `Minimize ${label}` : `Open ${label}`}>
                  <button
                    type="button"
                    data-testid="window-tab-toggle"
                    aria-pressed={shown}
                    aria-label={`${WINDOW_KIND_LABEL[w.kind]}: ${label}${shown ? ' (shown)' : ' (minimized)'}`}
                    // The second click of a double-click is the close gesture, not a second toggle.
                    onClick={(e) => {
                      if (e.detail > 1) return;
                      useWindowStore.getState().toggle(w.id);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-[6px] rounded-[11px] py-1 pl-[4px] pr-[11px] text-left text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="min-w-0 truncate">{label}</span>
                    <SlotKey n={firstSlot ? firstSlot + i : undefined} />
                  </button>
                </Tip>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
